/**
 * EndCallStatusDialog Component
 *
 * Shown to the provider after ending a video call.
 * Allows them to mark the appointment as completed or no-show
 * before navigating away.
 */

import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  CheckCircle2,
  UserX,
  PhoneOff,
  Loader2,
} from 'lucide-react'
import { cn } from '@/lib/utils'

export type EndCallStatus = 'completed' | 'no_show'

interface EndCallStatusDialogProps {
  open: boolean
  onConfirm: (status: EndCallStatus) => void | Promise<void>
  patientName?: string
}

const statusOptions: {
  value: EndCallStatus
  label: string
  description: string
  icon: React.ElementType
  iconColor: string
  bgColor: string
  borderColor: string
  selectedBorder: string
}[] = [
  {
    value: 'completed',
    label: 'Completed',
    description: 'The visit was completed successfully',
    icon: CheckCircle2,
    iconColor: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/20',
    selectedBorder: 'border-emerald-500/70',
  },
  {
    value: 'no_show',
    label: 'No Show',
    description: 'The patient did not attend the visit',
    icon: UserX,
    iconColor: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/20',
    selectedBorder: 'border-amber-500/70',
  },
]

const EndCallStatusDialog: React.FC<EndCallStatusDialogProps> = ({
  open,
  onConfirm,
  patientName,
}) => {
  const [selected, setSelected] = useState<EndCallStatus>('completed')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleConfirm = async () => {
    setIsSubmitting(true)
    try {
      await onConfirm(selected)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-[420px] p-0 gap-0 overflow-hidden border border-slate-700/50 bg-[#0f1419] rounded-2xl shadow-2xl shadow-black/60 [&>button]:hidden"
      >
        {/* Header */}
        <div className="relative px-5 pt-5 pb-4">
          <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-brand-teal/60 to-transparent" />

          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#1a2530] flex items-center justify-center border border-slate-600/40">
              <PhoneOff className="h-5 w-5 text-slate-300" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-white">
                Call Ended
              </h2>
              <p className="text-slate-400 text-xs mt-0.5">
                {patientName
                  ? `How did the visit with ${patientName} go?`
                  : 'Update the appointment status'}
              </p>
            </div>
          </div>
        </div>

        {/* Status Options */}
        <div className="px-5 pb-4 space-y-2">
          {statusOptions.map((option) => {
            const Icon = option.icon
            const isSelected = selected === option.value
            return (
              <button
                key={option.value}
                onClick={() => setSelected(option.value)}
                disabled={isSubmitting}
                className={cn(
                  'w-full p-4 rounded-xl border transition-all duration-200 text-left flex items-center gap-3.5',
                  isSelected
                    ? `${option.bgColor} ${option.selectedBorder} ring-1 ring-inset ${option.selectedBorder.replace('border-', 'ring-')}`
                    : 'bg-[#141c24] border-slate-700/40 hover:border-slate-600/60 hover:bg-[#182029]',
                )}
              >
                <div
                  className={cn(
                    'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border',
                    isSelected
                      ? `${option.bgColor} ${option.borderColor}`
                      : 'bg-[#1a2530] border-slate-700/50',
                  )}
                >
                  <Icon
                    className={cn(
                      'h-4 w-4',
                      isSelected ? option.iconColor : 'text-slate-400',
                    )}
                  />
                </div>
                <div className="min-w-0">
                  <h4
                    className={cn(
                      'font-medium text-[13px]',
                      isSelected ? 'text-white' : 'text-slate-300',
                    )}
                  >
                    {option.label}
                  </h4>
                  <p className="text-slate-400 text-xs mt-0.5">
                    {option.description}
                  </p>
                </div>
                {/* Radio indicator */}
                <div className="ml-auto flex-shrink-0">
                  <div
                    className={cn(
                      'w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all',
                      isSelected
                        ? `${option.selectedBorder} ${option.bgColor}`
                        : 'border-slate-600',
                    )}
                  >
                    {isSelected && (
                      <div
                        className={cn(
                          'w-2 h-2 rounded-full',
                          option.iconColor.replace('text-', 'bg-'),
                        )}
                      />
                    )}
                  </div>
                </div>
              </button>
            )
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 bg-[#0c1015] border-t border-slate-700/40">
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="w-full h-10 bg-brand-teal hover:bg-brand-teal/90 text-white font-medium text-sm transition-all duration-200"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              'Confirm & Close'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default EndCallStatusDialog
